import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { ApiClient, ApiError, defaultApiBase } from "../api/client";
import type { User } from "../api/types";
import { setLang, useLang, useT } from "../i18n";
import { useApp } from "../store";
import { IconLogo } from "../ui/icons";

type Mode = "login" | "signup" | "token";

function errorMessage(e: unknown, t: ReturnType<typeof useT>): string {
  if (e instanceof ApiError) {
    if (e.tag === "AUTHENTICATION_ERROR" || e.status === 401) return t("wrongCredentials");
    return e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

export function AuthPage({ mode }: { mode: Mode }) {
  const t = useT();
  const lang = useLang();
  const navigate = useNavigate();
  const setSession = useApp((s) => s.setSession);
  const [apiBase, setApiBase] = useState(defaultApiBase());
  const [showServer, setShowServer] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [invite, setInvite] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const base = apiBase.replace(/\/+$/, "");
  const finish = async (user: User, tok: string, kind: "login" | "token") => {
    try {
      if (base !== defaultApiBase()) localStorage.setItem("ld.apiBase", base);
    } catch {
      // ignore
    }
    await setSession({ apiBase: base, token: tok, userId: user.id, email: user.email, kind });
    navigate("/app/today", { replace: true });
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (mode === "token") {
        const tok = token.trim();
        const user = await new ApiClient(base, tok).get<User>("/user");
        await finish(user, tok, "token");
        return;
      }
      const client = new ApiClient(base, null);
      const user =
        mode === "login"
          ? await client.login(email.trim(), password)
          : await client.register({
              email: email.trim(),
              password,
              full_name: fullName.trim() || undefined,
              invite_code: invite.trim() || undefined,
              lang: lang === "zh" ? "zh_CN" : "en",
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
      if (!user.token) throw new Error("No token in response");
      if (mode === "signup" && fullName.trim() && !user.full_name) user.full_name = fullName.trim();
      await finish(user, user.token, "login");
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 405) && mode !== "token") {
        setUnavailable(true);
      } else if (mode === "token" && e instanceof ApiError && e.isAuth) {
        setError(t("invalidToken"));
      } else {
        setError(errorMessage(e, t));
      }
    } finally {
      setBusy(false);
    }
  };

  const title = mode === "login" ? t("loginTitle") : mode === "signup" ? t("signupTitle") : t("useToken");
  return (
    <div className="auth">
      <div className="auth-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div className="brand">
            <IconLogo size={28} />
            {t("appName")}
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ width: "auto", height: 28 }}
            onClick={() => setLang(lang === "zh" ? "en" : "zh")}
          >
            {lang === "zh" ? "English" : "中文"}
          </button>
        </div>
        <h1>{title}</h1>
        {unavailable && (
          <div className="error-text" style={{ marginBottom: 12 }} data-testid="login-unavailable">
            {t("loginUnavailable")}{" "}
            <Link to="/auth/token" className="link">
              {t("useToken")}
            </Link>
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          {mode === "token" ? (
            <label className="field">
              <span>{t("tokenLabel")}</span>
              <input
                className="input"
                type="password"
                autoComplete="off"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                autoFocus
                data-testid="token-input"
              />
              <span className="small muted">{t("tokenHint")}</span>
            </label>
          ) : (
            <>
              {mode === "signup" && (
                <label className="field">
                  <span>{t("fullName")}</span>
                  <input
                    className="input"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    autoComplete="name"
                  />
                </label>
              )}
              <label className="field">
                <span>{t("email")}</span>
                <input
                  className="input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  autoFocus
                  data-testid="email-input"
                />
              </label>
              <label className="field">
                <span>{t("password")}</span>
                <input
                  className="input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  data-testid="password-input"
                />
                {mode === "signup" && <span className="small muted">{t("passwordHint")}</span>}
              </label>
              {mode === "signup" && (
                <label className="field">
                  <span>{t("inviteCode")}</span>
                  <input
                    className="input"
                    value={invite}
                    onChange={(e) => setInvite(e.target.value)}
                    data-testid="invite-input"
                  />
                </label>
              )}
            </>
          )}
          {error && (
            <div className="error-text" style={{ marginBottom: 12 }} data-testid="auth-error">
              {error}
            </div>
          )}
          <button type="submit" className="btn btn-primary" disabled={busy} data-testid="auth-submit">
            {mode === "login" ? t("loginButton") : mode === "signup" ? t("signupButton") : t("continue")}
          </button>
        </form>
        <div className="auth-alt">
          {mode === "login" && (
            <>
              <div>
                <Link to="/auth/token" data-testid="use-token-link">
                  {t("useToken")}
                </Link>
              </div>
              <div style={{ marginTop: 10 }}>
                {t("noAccount")} <Link to="/auth/signup">{t("signupTitle")}</Link>
              </div>
            </>
          )}
          {mode === "signup" && (
            <div>
              {t("haveAccount")} <Link to="/auth/login">{t("loginTitle")}</Link>
            </div>
          )}
          {mode === "token" && (
            <div>
              <Link to="/auth/login">{t("loginTitle")}</Link>
            </div>
          )}
          <div style={{ marginTop: 18 }}>
            <button
              type="button"
              className="small"
              onClick={() => setShowServer(!showServer)}
              style={{ color: "var(--text-3)", textDecoration: "none" }}
            >
              {t("server")}: {new URL(base || "https://x").host}
            </button>
            {showServer && (
              <input
                className="input"
                style={{ marginTop: 8 }}
                value={apiBase}
                onChange={(e) => setApiBase(e.target.value)}
                data-testid="server-input"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
